// Leitura dos campos da carga compartilhada por Torre e Vincular carga.
// A torre pediu (out/2026) que cada carga mostre, além do básico, se é
// urgente, prioridade, crítica e se tem frete especial — e que o horário que o
// gestor SOLICITA apareça separado do horário de coleta CONFIRMADO.

export interface CampoCarga {
  data_hora_coleta?: string | null;
  data_hora_solicitada?: string | null;
  hora_agendamento?: string | null;
  urgente?: boolean | null;
  prioridade?: boolean | null;
  critica?: boolean | null;
  frete_especial?: string | null;
  id?: number;
}

const DIAS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

/**
 * "2026-10-29T13:30" e "2026-09-01 22:00:00" → "2026-10-29 13:30". A coluna é
 * texto e convive com os dois formatos; é a mesma normalização do ORDER BY
 * da API (COLETA_ORDEM_SQL). Sem horário → null.
 */
export function chaveColeta(v: unknown): string | null {
  const s = typeof v === "string" ? v.trim() : "";
  if (!s) return null;
  return s.replace("T", " ").slice(0, 16);
}

/** "qui 29/10 13:30". Fatia a string: Date leria como UTC e mudaria o dia. */
export function fmtColetaDia(v: unknown): string {
  const k = chaveColeta(v);
  if (!k || !/^\d{4}-\d{2}-\d{2}/.test(k)) return k ?? "sem data";
  const [a, m, d] = [Number(k.slice(0, 4)), Number(k.slice(5, 7)), Number(k.slice(8, 10))];
  const dia = DIAS[new Date(Date.UTC(a, m - 1, d)).getUTCDay()];
  const hora = k.slice(11, 16);
  return `${dia} ${k.slice(8, 10)}/${k.slice(5, 7)}${hora ? ` ${hora}` : ""}`;
}

/**
 * Sequência de coleta: dia e hora, a próxima primeiro; sem horário no fim.
 * Espelha a ordem da API para a aba "agendadas" — reordenar aqui garante a
 * sequência mesmo se a lista vier de uma busca ou de um cache anterior.
 */
export function ordenarPorColeta<T extends CampoCarga>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ka = chaveColeta(a.data_hora_coleta);
    const kb = chaveColeta(b.data_hora_coleta);
    if (ka !== kb) return ka == null ? 1 : kb == null ? -1 : ka.localeCompare(kb);
    const ha = (a.hora_agendamento ?? "").trim();
    const hb = (b.hora_agendamento ?? "").trim();
    if (ha !== hb) return !ha ? 1 : !hb ? -1 : ha.localeCompare(hb);
    return (a.id ?? 0) - (b.id ?? 0);
  });
}

export interface Marcador { chave: string; label: string; cls: string }

/** Selos da carga, do mais grave ao mais brando. */
export function marcadores(c: CampoCarga): Marcador[] {
  const out: Marcador[] = [];
  if (c.critica) out.push({ chave: "critica", label: "Crítica", cls: "bg-red-600 text-white" });
  if (c.urgente) out.push({ chave: "urgente", label: "Urgente", cls: "bg-red-100 text-red-800 ring-1 ring-red-300" });
  if (c.prioridade) out.push({ chave: "prioridade", label: "Prioridade", cls: "bg-amber-100 text-amber-900 ring-1 ring-amber-300" });
  const fe = (c.frete_especial ?? "").trim();
  if (fe) out.push({ chave: "frete", label: fe === "FRETE ESPECIAL" ? "Frete especial" : fe, cls: "bg-violet-100 text-violet-800 ring-1 ring-violet-300" });
  return out;
}

export type SituacaoHorario = "sem-pedido" | "aguardando" | "confirmado" | "alterado";

/**
 * Pedido do gestor × confirmação da programação:
 *  - aguardando: pediu e a torre ainda não pôs horário de coleta;
 *  - confirmado: coleta igual ao pedido;
 *  - alterado:   a torre confirmou outro horário.
 */
export function situacaoHorario(c: CampoCarga): SituacaoHorario {
  const pedido = chaveColeta(c.data_hora_solicitada);
  const coleta = chaveColeta(c.data_hora_coleta);
  if (!pedido) return "sem-pedido";
  if (!coleta) return "aguardando";
  return pedido === coleta ? "confirmado" : "alterado";
}

/**
 * Valor para <input type="datetime-local">: "2026-10-29 13:30:00" →
 * "2026-10-29T13:30". Fatia a string (sem Date) pelo mesmo motivo de
 * fmtColetaDia. Vazio ou sem hora → "".
 */
export function paraInputHorario(v: unknown): string {
  const k = chaveColeta(v);
  return k && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(k) ? k.replace(" ", "T") : "";
}
